import { useMemo } from "react";
import { Link } from "react-router-dom";
import { useBoard } from "@/hooks/useBoard";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { cn } from "@/lib/utils";
import statusBaked from "@/data/statusBoard.json"; // offline floor for the top band (ingest-status.py regenerates)

// ── Top status band (JOB-20261023-OS-TOPBAR, 2026-10-23) ────────────────
// ONE hairline row at the very top of the shell, mounted in AppNav so it rides
// every screen. Two live facts and nothing else: which Ollama accounts are
// cooling and how much of their weekly allowance is spent, plus the last five
// days of sales. It extends the nav bar — it is not a dashboard.
//
// Truth rules (the band never invents a number):
//   • weekly allowance unknown → "—", never 0%
//   • a degraded half → "no data" with a muted flag, never fabricated zeros
//   • a quiet sales day → a stub, never a $0 bar pretending to be a sale
//
// Live board when the API answers, the baked snapshot when it does not.

const BAKED_ITEM =
  (statusBaked.items || []).find((r) => r.slug === "status:topbar") || null;

const money = (usd) => {
  if (usd === null || usd === undefined || Number.isNaN(Number(usd)))
    return "—";
  const n = Number(usd);
  const cents = Math.abs(n % 1) > 0.004;
  return `$${n.toLocaleString("en-US", {
    minimumFractionDigits: cents ? 2 : 0,
    maximumFractionDigits: cents ? 2 : 0,
  })}`;
};

// Jeff 10/25: whole percentages — the tenth never changed a decision.
const pctText = (pct) =>
  pct === null || pct === undefined || Number.isNaN(Number(pct))
    ? "—"
    : `${Math.round(Number(pct))}%`;

// Kit 10/26: whole dollars for the band's headline money — a 30-day total
// carries "$10,728", not "$10,727.99". (Detail tables keep money()'s cents.)
const moneyWhole = (usd) =>
  usd === null || usd === undefined || Number.isNaN(Number(usd))
    ? "—"
    : `$${Math.round(Number(usd)).toLocaleString("en-US")}`;

// Jeff 10/26: cycle % = live requests ÷ the account's REAL weekly allowance
// (MAX plan = 32,768/wk, PRO plan = 8,111/wk — read off Jeff's ollama.com
// dashboard, refreshed by hand only if the plan changes). Jeff 10/29 (2nd
// ruling): the band shows ONLY a % + color — no "Limit hit", no raw counts.
// The 429 cap overrides to force 100%; clamp keeps the % in 0..100 even if
// request counts momentarily exceed the allowance. Unknown requests → "—".
const WEEKLY_ALLOWANCE = { max: 32768, pro: 8111 };
export const cyclePct = (a) => {
  if (a == null || a.limitHit) return 100;
  const n = Number(a.cycleUsage);
  if (a.cycleUsage == null || Number.isNaN(n)) return null;
  const allow = WEEKLY_ALLOWANCE[(a.tier || "").toLowerCase()];
  if (!allow) return null;
  return Math.max(0, Math.min(100, Math.round((n / allow) * 100)));
};

// Jeff 10/25: every key shows its health color right on the top line —
// green = good to go, amber = cooling, gray = unknown (never a fake green).
// 2026-10-29: a weekly-limit hit (ollama.com's own 429, via backend limitHit)
// is amber too — the strongest cooling state there is.
const keyTone = (a) =>
  a.cooling || a.limitHit ? "amber" : a.healthy ? "green" : "unknown";
const TONE_DOT = {
  green: "bg-os-green",
  amber: "bg-os-amber",
  unknown: "bg-os-border-strong",
};
const TONE_TEXT = {
  green: "text-os-green",
  amber: "text-os-amber",
  unknown: "text-os-ink-2",
};

// "2026-10-22" → "Oct 22" (parsed as UTC so the day never slides a day).
const dayLabel = (day) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(day || ""));
  if (!m) return String(day || "");
  return new Date(
    Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])),
  ).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
};

// "2026-10-23T19:39:51Z" → "1:39 PM" in the reader's own clock.
const clockTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
};

// Relative age ("2 min ago") answers "can I trust this?" faster than a bare clock
// (council 2026-10-24). Amber when older than 20 minutes, plain gray otherwise.
const STALE_AFTER_MIN = 20;
const ageLabel = (iso) => {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  const mins = Math.max(0, Math.round((Date.now() - t) / 60000));
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  return `${Math.round(mins / 60)}h ago`;
};
const isStale = (iso) => {
  if (!iso) return false;
  const t = new Date(iso).getTime();
  return !Number.isNaN(t) && Date.now() - t > STALE_AFTER_MIN * 60000;
};

// "KEY_1" → "K1" on a narrow strip: the prefix goes, the key identity stays
// (a bare "1" beside a percentage reads as a list index). Idempotent: a label
// already in K-form ("K1", the collector's current convention) passes through
// unchanged — never "KK1".
const shortLabel = (label) => {
  const s = String(label || "");
  if (/^K\d/.test(s)) return s;
  return s.replace(/^KEY_/, "").replace(/^(.)/, (c) => `K${c}`);
};

const accountTip = (a) => {
  // Detailed provenance lives in the HOVER TIP, not the band readout.
  // Jeff 10/29 (2nd ruling): band = % + color only; tip keeps the full account
  // facts: real cycle requests ÷ documented-plan allowance, cap/cooling state.
  const pct = a.limitHit ? 100 : cyclePct(a);
  const bits = [
    `${a.label} — ${
      pct != null
        ? `${pct}% of weekly allowance (${
            a.limitHit
              ? "capped by ollama.com 429"
              : a.cooling
                ? "cooling"
                : "healthy"
          })`
        : "usage unavailable"
    }`,
  ];
  if (a.cycleUsage != null)
    bits.push(`${Number(a.cycleUsage).toLocaleString("en-US")} requests this cycle`);
  if (a.sessionUsage !== null && a.sessionUsage !== undefined)
    bits.push(`session ${Number(a.sessionUsage).toLocaleString("en-US")}`);
  if (a.sessionResetsIn) bits.push(`session resets in ${a.sessionResetsIn}`);
  if (a.cycleResetsIn) bits.push(`cycle resets in ${a.cycleResetsIn}`);
  return bits.join(" · ");
};

const aiTip = (or) =>
  `OpenRouter burn, 7 days: ${money(or.total7dUsd)}${
    or.topModel
      ? ` · top model ${or.topModel.model} ${money(or.topModel.usd)}`
      : ""
  }`;

const ClusterWord = ({ children, always }) => (
  <span
    className={cn(
      "shrink-0 font-mono text-[0.625rem] uppercase tracking-[0.14em] text-os-ink-4",
      always ? "inline" : "hidden sm:inline",
    )}
  >
    {children}
  </span>
);

// Every cluster is a door (council 2026-10-24): the whole cluster links to its own
// /status section, with a chevron that shows on hover. Keyboard + touch get the real
// link, not just a hover tooltip.
const ClusterLink = ({ to, children, testid }) => (
  <Link
    to={to}
    data-testid={testid}
    className="group flex shrink-0 items-center gap-1 whitespace-nowrap rounded-chip hover:bg-os-paper focus-visible:bg-os-paper"
  >
    {children}
    <span
      aria-hidden="true"
      className="text-os-ink-4 opacity-0 transition-opacity duration-150 group-hover:opacity-100 group-focus-visible:opacity-100"
    >
      ›
    </span>
  </Link>
);

export const StatusBand = () => {
  const board = useBoard("status");
  const desktop = useMediaQuery("(min-width: 1024px)");
  const roomy = useMediaQuery("(min-width: 1280px)");

  const item = useMemo(() => {
    // Live board → the live item, or nothing at all (a live board without the
    // item is "no data", never the stale baked snapshot). No live board → the
    // baked floor, so the band still renders offline.
    if (board.live)
      return (
        (board.items || []).find((r) => r.slug === "status:topbar") || null
      );
    return BAKED_ITEM;
  }, [board.items, board.live]);

  const payload = item?.payload || {};
  const ollama = payload.ollama || null;
  const sales = payload.sales || null;
  const openrouter = payload.openrouter || null;
  const revenue = payload.revenue || null;
  const accounts = Array.isArray(ollama?.accounts) ? ollama.accounts : [];
  const days = Array.isArray(sales?.days) ? sales.days : [];

  // A half is usable only when it is present AND not degraded.
  const accountsLive =
    Boolean(ollama) && !ollama.degraded && accounts.length > 0;
  const salesLive = Boolean(sales) && !sales.degraded && days.length > 0;

  const cooling = accounts.filter((a) => a.cooling).length;
  const healthy = accounts.filter((a) => a.healthy).length;
  // ── the money cluster (Jeff 10/26: Sales + Revenue consolidated) ──
  // ONE number: revenue of the last 30 days out of the revenue ingest, which
  // already merges Stripe AND PayStand (Job-20261026 fixed the PayStand feed).
  // Sales was the same Stripe money in a 5-day window; the by-brand breakouts
  // live in the hover + the /status detail page now.
  const revDays = Array.isArray(revenue?.days) ? revenue.days : [];
  // Kit 10/26 truth fix: the label says /30d, so the number must BE 30 days.
  // The sibling lane summed revenue.days (a 5-day bucket array) into a value
  // labeled /30d — $8,288 understated the true $10,727.99 (brands30d window
  // = Stripe + PayStand merged). Same source, right window.
  const revTotal30 = Object.values(revenue?.brands30d || {}).reduce(
    (s, v) => s + (Number(v.usd) || 0),
    0,
  );
  const maxRevUsd = (revDays || []).reduce(
    (m, d) =>
      Math.max(
        m,
        Object.values(d.brands || {}).reduce((x, v) => x + (Number(v) || 0), 0),
      ),
    0,
  );
  const revBrandTop = Object.entries(revenue?.brands30d || {})
    .filter(([, v]) => (Number(v.usd) || 0) > 0)
    .slice(0, 3)
    .map(([b, v]) => `${b} ${money(v.usd)}`)
    .join(" · ");
  const revTip = () =>
    `Revenue, 30 days: ${money(revTotal30)} — Stripe + PayStand${
      revBrandTop ? ` · ${revBrandTop}` : ""
    }`;
  const orDays = Array.isArray(openrouter?.days) ? openrouter.days : [];
  const orLive =
    Boolean(openrouter) && !openrouter.degraded && orDays.length > 0;
  const revLive = Boolean(revenue) && !revenue.degraded;
  const asOf = ollama?.asOf || sales?.asOf || payload.pushedAt || null;

  return (
    <div
      data-testid="status-band"
      role="group"
      aria-label="Accounts and revenue"
      className="flex h-auto flex-col gap-1 border-b border-os-border bg-white px-4 py-1.5 font-mono text-[0.6875rem] leading-none text-os-ink-2 sm:h-9 sm:flex-row sm:items-center sm:gap-4 sm:divide-x sm:divide-os-border/40 sm:overflow-hidden sm:px-4 md:px-6 sm:py-0 [&>*]:px-3 first:[&>*]:pl-0 sm:[&>*]:py-0"
    >
      {/* ── accounts ── */}
      <div className="flex min-w-0 items-center gap-2">
        <ClusterWord>Ollama</ClusterWord>
        {accountsLive ? (
          <ClusterLink
            to="/status#status-proxies"
            testid="status-band-proxies-link"
          >
            {desktop ? (
              <ul
                data-testid="status-band-accounts"
                className="flex min-w-0 items-center gap-2 overflow-hidden"
              >
                {accounts.map((a, i) => (
                  <li
                    key={a.label}
                    data-testid={`status-band-account-${a.label}`}
                    title={accountTip(a)}
                    className="flex shrink-0 items-center gap-1 whitespace-nowrap"
                  >
                    {i > 0 ? (
                      <span aria-hidden="true" className="text-os-ink-4">
                        ·
                      </span>
                    ) : null}
                    <span
                      data-testid={`status-band-key-${a.label}`}
                      data-tone={keyTone(a)}
                      aria-hidden="true"
                      className={cn(
                        "h-1.5 w-1.5 rounded-full",
                        TONE_DOT[keyTone(a)],
                      )}
                    />
                    <span
                      data-testid={`status-band-key-label-${a.label}`}
                      className={TONE_TEXT[keyTone(a)]}
                    >
                      {roomy ? a.label : shortLabel(a.label)}
                    </span>
                    <span
                      data-testid={`status-band-pct-${a.label}`}
                      title={accountTip(a)}
                      className={cn(
                        "shrink-0 whitespace-nowrap font-semibold tabular-nums",
                        TONE_TEXT[keyTone(a)],
                      )}
                    >
                      {/* Jeff 10/29 (2nd ruling): % only — never words, never raw counts.
                          Color carries the state: green = fine, amber = capped/cooling. */}
                      {cyclePct(a) != null ? `${cyclePct(a)}%` : "—"}
                    </span>
                  </li>
                ))}
              </ul>
            ) : null}
            {/* Jeff 10/27: mobile shows the real per-key data on its own row(s).
                Exact cycle requests are the primary number; ~% is the estimate. */}
            {!desktop && accountsLive ? (
              <ul
                data-testid="status-band-accounts-mobile"
                className="flex w-full flex-wrap items-center gap-x-3 gap-y-1 sm:hidden"
              >
                {accounts.map((a) => (
                  <li
                    key={a.label}
                    data-testid={`status-band-account-mobile-${a.label}`}
                    title={accountTip(a)}
                    className="flex shrink-0 items-center gap-1 whitespace-nowrap"
                  >
                    <span
                      aria-hidden="true"
                      className={cn(
                        "h-1.5 w-1.5 rounded-full",
                        TONE_DOT[keyTone(a)],
                      )}
                    />
                    <span className={TONE_TEXT[keyTone(a)]}>
                      {shortLabel(a.label)}
                    </span>
                    <span className={cn("font-semibold tabular-nums", TONE_TEXT[keyTone(a)])}>
                      {cyclePct(a) != null ? `${cyclePct(a)}%` : "—"}
                    </span>
                    <span
                      data-testid={`status-band-pct-mobile-${a.label}`}
                      className={cn("tabular-nums", TONE_TEXT[keyTone(a)])}
                    >
                      {cyclePct(a) != null ? `${cyclePct(a)}%` : "—"}
                    </span>
                  </li>
                ))}
              </ul>
            ) : null}
          </ClusterLink>
        ) : (
          <span
            data-testid="status-band-ollama-nodata"
            className="shrink-0 text-os-ink-4"
          >
            no data
          </span>
        )}
      </div>

      {/* ── revenue — the ONE money cluster (Jeff 10/26: consolidate Sales +
          Revenue; one bold number, categories live in the tooltip) ── */}
      <div className="flex min-w-0 w-full items-center gap-2 sm:w-auto">
        <ClusterWord>Revenue</ClusterWord>
        {revLive ? (
          <ClusterLink
            to="/status#status-revenue"
            testid="status-band-revenue"
          >
            {desktop ? (
              <div
                data-testid="status-band-rev-bars"
                aria-hidden="true"
                className="flex h-5 shrink-0 items-end gap-[3px]"
              >
                {revDays.map((d) => {
                  const usd = Object.values(d.brands || {}).reduce(
                    (s, v) => s + (Number(v) || 0),
                    0,
                  );
                  if (!usd) return null; // no drawn stub for a zero day (council)
                  const h = Math.max(
                    5,
                    Math.round((usd / (maxRevUsd || 1)) * 20),
                  );
                  const names = Object.keys(d.brands || {}).join(", ");
                  return (
                    <span
                      key={d.day}
                      data-testid="status-band-bar"
                      data-day={d.day}
                      title={`${dayLabel(d.day)} — ${money(usd)}${names ? ` · ${names}` : ""}`}
                      style={{ height: `${h}px` }}
                      className="w-[3px] rounded-segment bg-os-ink"
                    />
                  );
                })}
              </div>
            ) : null}
            <span
              data-testid="status-band-revenue-total"
              title={revTip(revenue)}
              className="shrink-0 whitespace-nowrap font-semibold text-os-ink"
            >
              {desktop ? `${moneyWhole(revTotal30)} /30d` : moneyWhole(revTotal30)}
            </span>
            <span data-testid="status-band-revenue-alt" className="sr-only">
              {`revenue last 30 days ${moneyWhole(revTotal30)} across Stripe and PayStand`}
            </span>
          </ClusterLink>
        ) : (
          <span
            data-testid="status-band-revenue-nodata"
            className="shrink-0 text-os-ink-4"
          >
            no data
          </span>
        )}
      </div>

      {/* ── AI burn (OpenRouter, $/day mini-bars) — bold number (Jeff 10/26) ── */}
      <div className="flex min-w-0 w-full items-center gap-2 sm:w-auto">
        <ClusterWord>AI burn</ClusterWord>
        {orLive ? (
          <ClusterLink to="/status#status-burn" testid="status-band-ai-link">
            {desktop ? (
              <div
                data-testid="status-band-ai-bars"
                aria-hidden="true"
                className="flex h-5 shrink-0 items-end gap-[3px]"
              >
                {orDays.map((d, i) => {
                  const usd = Number(d.usd) || 0;
                  if (!usd) return null; // no picket-fence ticks for zero days (council)
                  const maxOr = Math.max(
                    ...orDays.map((x) => Number(x.usd) || 0),
                    0.0001,
                  );
                  const h = Math.max(5, Math.round((usd / maxOr) * 20));
                  return (
                    <span
                      key={d.day || i}
                      data-testid="status-band-ai-bar"
                      data-day={d.day}
                      title={`${dayLabel(d.day)} — OpenRouter ${money(usd)}`}
                      style={{ height: `${h}px` }}
                      className="w-[3px] rounded-segment bg-os-ink"
                    />
                  );
                })}
              </div>
            ) : null}
            <span
              data-testid="status-band-ai-total"
              title={aiTip(openrouter)}
              className="shrink-0 whitespace-nowrap font-semibold text-os-ink"
            >
              {desktop
                ? `${money(openrouter.total7dUsd)} (7d)`
                : orDays.length
                  ? money(orDays[orDays.length - 1].usd)
                  : "—"}
            </span>
          </ClusterLink>
        ) : (
          <span
            data-testid="status-band-ai-nodata"
            className="shrink-0 text-os-ink-4"
          >
            no data
          </span>
        )}
      </div>

      {/* ── freshness + mobile wordmark row (Jeff 10/27: multi-row mobile band) ── */}
      <div className="flex w-full items-center justify-between sm:contents">
        {!desktop ? (
          <Link
            to="/"
            data-testid="status-band-wordmark"
            className="shrink-0 text-[0.8125rem] font-semibold tracking-[-0.02em] text-os-ink sm:hidden"
          >
            Agentic OS
          </Link>
        ) : null}
        <span
          data-testid="status-band-asof"
          title={
            asOf
              ? `Data as of ${new Date(asOf).toLocaleString("en-US")}`
              : "No timestamp"
          }
          data-stale={isStale(asOf) ? "true" : "false"}
          className={cn(
            "shrink-0 whitespace-nowrap sm:ml-auto sm:pl-3",
            !desktop && "pl-0",
            isStale(asOf) ? "text-os-amber" : "text-os-ink-4",
          )}
        >
          {ageLabel(asOf) || clockTime(asOf)}
        </span>
      </div>
    </div>
  );
};
